import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { addToQueue, getQueue, removeFromQueue, updateQueueItem, cacheAddressList, searchAddresses, getCachedAddress, cacheAddress, hasQueueHeadroom, queueItemBytes } from './db';
import apiFetch from './api';
import { runSyncHandler, isRetryableOfflineResponse } from './offlineWork';

const OfflineContext = createContext();

export const useOffline = () => useContext(OfflineContext);

// Defined alongside the sync handlers that classify responses; re-exported here
// because callers already import it from this module.
export { isRetryableOfflineResponse };

export const isRetryableOfflineError = (error) => {
  if (!error) return false;
  if (error.retryable === true) return true;
  if (typeof error.status === 'number') return isRetryableOfflineResponse(error);
  const message = String(error.message || error).toLowerCase();
  return (
    message.includes('network') ||
    message.includes('timed out') ||
    message.includes('timeout') ||
    message.includes('failed to fetch') ||
    message.includes('aborted') ||
    message.includes('load failed')
  );
};

const normalizeWorkType = (type) => {
  if (type === 'ADD_COMMENT') return 'comment';
  return type;
};

export const createClientMutationId = (prefix = 'web') => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${prefix}-${crypto.randomUUID()}`;
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

export const OfflineProvider = ({ children }) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [queue, setQueue] = useState([]);
  const [syncSummary, setSyncSummary] = useState(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncMessage, setLastSyncMessage] = useState('');
  const syncRunning = useRef(false);
  const [retryTick, setRetryTick] = useState(0);
  const { token } = useAuth();

  useEffect(() => {
    const loadQueue = async () => {
      try {
        const storedQueue = await getQueue();
        if (storedQueue && storedQueue.length > 0) {
          setQueue(storedQueue);
        }
      } catch (error) {
        console.error('Failed to load offline queue:', error);
      }
    };
    loadQueue();
  }, []);

  useEffect(() => {
    const handleOnline = () => { setIsOnline(true); setRetryTick((value) => value + 1); };
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const syncAddresses = useCallback(async () => {
    if (!token || !navigator.onLine) return;
    try {
      // Fetch simpler list for search
      // Background prefetch: a 401 here must not end the user's session.
      const response = await apiFetch(`/addresses/?limit=5000`, {}, { background: true }); // Adjust limit as needed
      if (response.ok) {
        const data = await response.json();
        if (Array.isArray(data)) {
          await cacheAddressList(data);
        }
      }
    } catch (e) {
      console.warn('Failed to background sync addresses:', e);
    }
  }, [token]);

  // Sync addresses on load if online
  useEffect(() => {
    if (isOnline && token) {
      // Small delay to not block initial render
      const timer = setTimeout(() => {
        syncAddresses();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [isOnline, token, syncAddresses]);


  const refreshSyncSummary = useCallback(async () => {
    if (!token || !navigator.onLine) return null;
    try {
      // Reconcile by exact saved mutation IDs before displaying account status.
      // Older servers can continue serving the summary during a rolling release.
      let response = await apiFetch('/sync-work/reconcile', { method: 'POST' }, { background: true });
      if (response.status === 404 || response.status === 405) {
        response = await apiFetch('/sync-work?status=active&limit=10', {}, { background: true });
      }
      if (response.ok) {
        const data = await response.json();
        setSyncSummary(data);
        return data;
      }
    } catch (error) {
      console.warn('Failed to load sync status:', error);
    }
    return null;
  }, [token]);

  const reportSyncWork = useCallback(async (item, status, details = {}) => {
    if (!token || !navigator.onLine) return;
    const payload = {
      client_id: String(item.clientId || item.id),
      work_type: normalizeWorkType(item.type),
      summary: item.summary || item.type,
      status,
      record_type: details.recordType,
      record_id: details.recordId,
      error_message: details.errorMessage,
      conflict_reason: details.conflictReason,
      device_label: 'web',
      queued_at: item.timestamp,
    };

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 8000);
    try {
      // Bookkeeping about the queue, not the user's work: never let it log out.
      const createResponse = await apiFetch('/sync-work', {
        method: 'POST',
        body: payload,
        signal: controller.signal,
      }, { background: true });
      if (createResponse.status === 409 || createResponse.status === 422 || status !== 'pending') {
        await apiFetch(`/sync-work/${encodeURIComponent(payload.client_id)}`, {
          method: 'PATCH',
          signal: controller.signal,
          body: {
            status,
            record_type: payload.record_type,
            record_id: payload.record_id,
            error_message: payload.error_message,
            conflict_reason: payload.conflict_reason,
          },
        }, { background: true });
      }
      refreshSyncSummary();
    } catch (error) {
      console.warn('Failed to report sync work:', error);
    } finally {
      clearTimeout(timer);
    }
  }, [token, refreshSyncSummary]);

  const queueAction = useCallback(async (action) => {
    const createdAt = new Date().toISOString();
    const newItem = {
      ...action,
      id: createClientMutationId('queue'),
      clientId: action.clientId || `web-${Date.now()}`,
      type: normalizeWorkType(action.type),
      timestamp: createdAt,
      status: 'pending',
      attempts: 0,
    };
    // Ask before writing: a quota error thrown here would lose the photo the
    // officer just took, and there is no way to get that moment back.
    const bytes = queueItemBytes(newItem);
    if (bytes > 0 && !(await hasQueueHeadroom(bytes))) {
      const message = 'Not enough space on this device to save more photos offline. Reconnect to upload what is queued.';
      setLastSyncMessage(message);
      throw new Error(message);
    }
    try {
      await addToQueue(newItem);
      setQueue((prev) => [...prev, newItem]);
      setLastSyncMessage('Saved on this device. Upload will continue automatically.');
      // Sync reports its own status; bookkeeping must not delay durable saving.
    } catch (error) {
      console.error('Error adding to offline queue:', error);
      throw error;
    }
  }, []);

  const removeTask = useCallback(async (id) => {
    try {
      await removeFromQueue(id);
      setQueue((prev) => prev.filter((item) => item.id !== id));
    } catch (error) {
      console.error('Error removing from offline queue:', error);
    }
  }, []);

  const syncQueue = useCallback(async ({ automatic = false } = {}) => {
    const report = (message) => {
      if (!automatic) setLastSyncMessage(message);
      return { message };
    };
    if (syncRunning.current) return report('An upload is already running. Keep this app open while it finishes.');
    if (!isOnline || !navigator.onLine) return report('This device is offline. Reconnect, then retry your saved uploads.');
    if (!token) return report('Sign in again to upload the work saved on this device.');
    if (queue.length === 0) return report('No saved uploads found in this browser. Account sync status may belong to another browser or device, or be awaiting confirmation.');
    const pending = queue.filter((item) => !automatic || (
      item.retryable !== false && (!item.nextRetryAt || item.nextRetryAt <= Date.now())
    ));
    if (!pending.length) return;
    syncRunning.current = true;

    setIsSyncing(true);
    setLastSyncMessage('Uploading saved field work...');
    let uploaded = 0;
    let failed = 0;
    let lastError = '';

    // Sequentially: a field device on a weak connection does better with one
    // upload at a time than with several competing for the same thin pipe.
    try {
      for (const item of pending) {
        let startedReport;
        setLastSyncMessage(`Uploading item ${uploaded + failed + 1} of ${pending.length}. Keep this app open.`);
        const slowTimer = setTimeout(() => {
          setLastSyncMessage('The upload is taking longer than expected. Keep this app open; your work is still saved on this device.');
        }, 30000);
        try {
          const syncingItem = { ...item, status: 'syncing', attempts: (item.attempts || 0) + 1 };
          await updateQueueItem(syncingItem);
          setQueue((prev) => prev.map((queued) => queued.id === item.id ? syncingItem : queued));
          // Upload starts immediately; status reporting runs alongside it.
          startedReport = reportSyncWork(syncingItem, 'syncing');

          // Every work type is replayed through the registry in offlineWork.js.
          // An unregistered type throws rather than falling through, so queued
          // work can never sit in "syncing" with nothing on its way up.
          const outcome = await runSyncHandler({ item: syncingItem, apiFetch });

          await startedReport;
          await reportSyncWork(syncingItem, 'completed', {
            recordType: outcome?.recordType,
            recordId: outcome?.recordId,
          });
          await removeTask(item.id);
          uploaded += 1;
          if (outcome?.event) window.dispatchEvent(outcome.event);
          setLastSyncMessage(outcome?.message || 'All queued field work uploaded.');
        } catch (error) {
          failed += 1;
          lastError = error.message || 'Upload could not be completed. Check your connection and retry.';
          console.error('Error syncing item:', item, error);
          const attempts = (item.attempts || 0) + 1;
          const failedItem = { ...item, status: 'failed', error: error.message, attempts,
            retryable: !error.isConflict && error.retryable !== false,
            nextRetryAt: Date.now() + Math.min(300000, 5000 * (2 ** Math.min(attempts - 1, 6))),
          };
          await updateQueueItem(failedItem);
          setQueue((prev) => prev.map((queued) => queued.id === item.id ? failedItem : queued));
          await startedReport;
          await reportSyncWork(failedItem, error.isConflict ? 'conflict' : 'failed', {
            errorMessage: error.message,
            conflictReason: error.conflictReason,
          });
          setLastSyncMessage(error.message || 'Some queued work still needs attention.');
          // Kept in the queue deliberately: a failed item is the officer's work,
          // and dropping it would be the silent loss this queue exists to prevent.
        } finally {
          clearTimeout(slowTimer);
        }
      }
      const message = failed
        ? `${uploaded} uploaded; ${failed} still saved on this device. ${lastError}`
        : `${uploaded} item${uploaded === 1 ? '' : 's'} uploaded successfully.`;
      setLastSyncMessage(message);
      return { message };
    } finally {
      setIsSyncing(false);
      syncRunning.current = false;
      refreshSyncSummary();
    }
  }, [queue, isOnline, token, removeTask, reportSyncWork, refreshSyncSummary]);


  // Auto-sync when coming online
  useEffect(() => {
    if (isOnline) {
      // Another device opening must never reset an in-progress upload.
      if (!isSyncing) syncQueue({ automatic: true });
    }
  }, [isOnline, token, isSyncing, syncQueue, refreshSyncSummary, retryTick]);

  useEffect(() => {
    refreshSyncSummary();
    const resume = () => {
      if (document.visibilityState === 'hidden') return;
      setIsOnline(navigator.onLine);
      setRetryTick((value) => value + 1);
    };
    const refreshOnResume = () => { resume(); refreshSyncSummary(); };
    const summaryTimer = setInterval(() => {
      if (document.visibilityState !== 'hidden') refreshSyncSummary();
    }, 30000);
    // A network can recover without any offline/online transition.
    const timer = setInterval(resume, 5000);
    window.addEventListener('focus', refreshOnResume);
    document.addEventListener('visibilitychange', refreshOnResume);
    return () => {
      clearInterval(timer);
      clearInterval(summaryTimer);
      window.removeEventListener('focus', refreshOnResume);
      document.removeEventListener('visibilitychange', refreshOnResume);
    };
  }, [refreshSyncSummary]);

  return (
    <OfflineContext.Provider value={{
      isOnline,
      queue,
      isSyncing,
      lastSyncMessage,
      syncSummary,
      syncQueue,
      refreshSyncSummary,
      queueAction,
      searchLocalAddresses: searchAddresses,
      getCachedAddress,
      cacheAddress
    }}>
      {children}
    </OfflineContext.Provider>
  );
};
